/**
 * NOTE (Ryan, 2026-02-03): IntegrationStatusCard
 *
 * Displays the connection status of an agent integration with Connect/Disconnect buttons.
 * Used by the agent to show integration status when users ask about connecting/disconnecting.
 */

import React, { useCallback, useState } from "react";

import { useNangoConnect } from "@agent-ui/hooks/useNangoConnect";
import { Button } from "@replo/design-system/components/button/Button";
import { getAgentIntegrationByKey } from "@v2/features/integrations/agentIntegrationDefinitions";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { agentIntegrationsRouter } from "@v2/trpc/routes/agentIntegrations";
import { AlertCircle } from "lucide-react";

interface IntegrationStatusCardProps {
  integrationKey: string;
  name: string;
  description: string;
  isConnected: boolean;
  connectionId?: string;
}

export function IntegrationStatusCard({
  integrationKey,
  name,
  description,
  isConnected: initialIsConnected,
  connectionId: initialConnectionId,
}: IntegrationStatusCardProps) {
  const projectId = useCurrentProjectId();
  const [isConnected, setIsConnected] = useState(initialIsConnected);
  const [connectionId, setConnectionId] = useState(initialConnectionId);
  const [error, setError] = useState<string | null>(null);

  const integrationDefinition = getAgentIntegrationByKey(integrationKey);
  const displayTitle = integrationDefinition?.title ?? name;
  const displayDescription = integrationDefinition?.description ?? description;
  const integrationLogoPath = integrationDefinition?.image;

  const { mutateAsync: deleteConnection, isPending: isDisconnecting } =
    agentIntegrationsRouter.deleteConnection.useMutation();

  const { connectMutation } = useNangoConnect({
    projectId: projectId ?? "",
  });
  const { mutateAsync: connect, isPending: isConnecting } = connectMutation;

  const handleConnect = useCallback(async () => {
    setError(null);
    try {
      const newConnectionId = await connect({ integrationKey });
      if (newConnectionId !== undefined) {
        setIsConnected(true);
        setConnectionId(newConnectionId);
      }
    } catch (connectError) {
      setError(
        connectError instanceof Error
          ? connectError.message
          : "Failed to connect",
      );
    }
  }, [connect, integrationKey]);

  const handleDisconnect = useCallback(async () => {
    if (!connectionId || !projectId) {
      if (!projectId) {
        setError("No active project selected");
      }
      return;
    }

    setError(null);

    try {
      await deleteConnection({ connectionId, projectId });
      setIsConnected(false);
      setConnectionId(undefined);
    } catch (disconnectError) {
      setError(
        disconnectError instanceof Error
          ? disconnectError.message
          : "Failed to disconnect",
      );
    }
  }, [connectionId, deleteConnection, projectId]);

  const cardGradientBackground =
    "linear-gradient(white, white) padding-box, var(--color-border) border-box";

  return (
    <div
      className="relative isolate flex w-full flex-col gap-3 rounded-xl border-0.5 border-transparent p-4"
      style={{ background: cardGradientBackground }}
    >
      <div className="absolute top-0 left-0 right-0 h-24 opacity-15 blur-xl pointer-events-none bg-[linear-gradient(120deg,#B7FEAE_0%,#FFD873_33%,#FE816E_66%,#DA6AF3_100%)]" />

      <div className="relative z-10 flex items-center justify-between gap-3">
        <div className="flex items-center justify-center size-10 rounded-lg bg-surface shrink-0">
          {integrationLogoPath ? (
            <img
              src={integrationLogoPath}
              alt={`${displayTitle} logo`}
              className="size-6 object-contain"
            />
          ) : (
            <span className="text-xs text-muted">?</span>
          )}
        </div>
        {isConnected ? (
          <Button
            variant="secondary"
            size="base"
            onClick={() => {
              void handleDisconnect();
            }}
            loading={isDisconnecting}
            disabled={isDisconnecting || isConnecting}
          >
            Disconnect
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="base"
            onClick={() => {
              void handleConnect();
            }}
            loading={isConnecting}
            disabled={isConnecting || isDisconnecting || !projectId}
          >
            Connect
          </Button>
        )}
      </div>

      <div className="relative z-10 flex flex-col gap-1 w-full">
        <span className="text-base font-semibold text-default">
          {displayTitle}
        </span>
        <span className="text-sm text-muted">{displayDescription}</span>
      </div>

      {error ? (
        <div className="relative z-10 flex items-center gap-2 text-xs text-rose bg-rose-soft px-3 py-2 rounded-md">
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      ) : null}
    </div>
  );
}
